import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Card, Container } from "reactstrap";

import styles from "Pages/Tools/MatchTools/MatchTools.module.css";
import useFetch from "Hooks/useFetch";
import useAlertContext from "Hooks/useAlertContext";
import MatchTools from "Pages/Tools/MatchTools/MatchTools";
import SelectModal from "Pages/Tools/MatchTools/Components/SelectModal/SelectModal";
import DimensionSpinner from "components/DimensionSpinner/DimensionSpinner";
import usePageTitle from "Hooks/usePageTitle";

export interface IMatchTool {
    description: string;
    method_name: string;
    parameters: string[];
    actions: {
        method_type: string;
        method_name: string;
        description: string;
        method_parameters: {
            system: string;
            parameters: string[];
        }[];
        /** A config-gated action the client has not switched on, shown greyed out (Projects, Companies/Vendors). */
        disabled?: boolean;
        disabled_reason?: string;
    }[];
}

export interface IMatchData {
    name: string;
    message?: string;
    /** Opt-in per tool: keys kept in row data but not rendered as columns. */
    hidden_columns?: string[];
    data_set_1: {
        name: string;
        data: {
            item_id: string;
            match_ids: string[];
            project_number: string;
            name: string;
        }[];
    };
    data_set_2: {
        name: string;
        data: {
            item_id: string;
            match_ids: string[];
            project_id: string;
            description: string;
        }[];
    };
}

export interface ISelection {
    project_number: string;
    name: string;
    [key: string]: string;
}

/** Higher order component wrapping MatchTools.tsx. Parses URL param, gets the available match tool
 * data that is spread across 2 or 3 provider service endpoints for systems tables, and then passes it
 * down to a match tool component.
 */
export default function MatchToolsSetup() {
    const [matchTool, setMatchTool] = useState<IMatchTool>();
    const [matchToolRecords, setMatchToolRecords] = useState<IMatchData>();
    /** The server's reason the tool data failed to load, shown in place of the grid */
    const [matchToolDataError, setMatchToolDataError] = useState<string>("");
    const [selectDataCacheTime, setSelectDataCacheTime] = useState<string>("");
    /** Data from the select action, from which a single item will have to be picked */
    const [selectData, setSelectData] = useState<ISelection[]>();
    /** Why the select action failed, if it did — shown in the modal so an error is never
     * indistinguishable from "no records" (dimension-mrzt3) */
    const [selectDataError, setSelectDataError] = useState<string>("");
    /** The selection from selectData */
    const [selection, setSelection] = useState<ISelection>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState({
        tools: false,
        toolData: false,
        selectData: false,
    });

    usePageTitle("Match Tools");
    const { get } = useFetch();
    const { newAlert } = useAlertContext();

    // Get tool name from the URL
    /** tool will be "match_projects" or something similar */
    const { tool } = useParams();

    const ignoreMatchToolData = useRef(false);

    const getMatchToolData = useCallback(
        async (url: string, reset_cache: boolean = false) => {
            setIsLoading((prev) => ({ ...prev, toolData: true }));

            if (reset_cache) {
                url += "?reset_cache=true";
            }

            const response = await get(url);

            let data: Response | IMatchData;
            try {
                data = await response.json();
            } catch {
                data = response;
            }
            console.log("Get data for tool response: ", data);

            if (ignoreMatchToolData.current) {
                return;
            }
            const records = data as IMatchData;
            // dimension-8bf5q: a provider that catches its own failure answers 200 with only
            // { message }, or with empty data sets plus a message (Get_Error_JsonForTables).
            // Successful payloads send message "". Rendering the first as records threw on
            // data_set_1.name and blanked the page.
            if (
                response.ok &&
                (records?.message || !records?.data_set_1 || !records?.data_set_2)
            ) {
                setMatchToolDataError(
                    records?.message || "Error loading data for match tool."
                );
            } else if (response.ok) {
                setMatchToolDataError("");
                setMatchToolRecords(records);

                const matchToolDataCacheTime =
                    response.headers.get("cached_at");
                if (matchToolDataCacheTime) {
                    setSelectDataCacheTime(matchToolDataCacheTime);
                }
            } else {
                setMatchToolDataError(
                    (data as { detail?: string })?.detail ||
                        (data as { title?: string })?.title ||
                        records?.message ||
                        `${response.status} ${response.statusText}`
                );
                newAlert({
                    message: "Error loading data for match tool.",
                    color: "danger",
                    seconds: 60
                });
            }
            setIsLoading((prev) => ({ ...prev, toolData: false }));
        },
        [get, newAlert]
    );

    // matchTool - Call endpoint to call provider service for info about the methods (parameters,
    // actions, link/unlink method names, etc)
    useEffect(() => {
        let ignore = false;
        getMatchTools();

        async function getMatchTools() {
            setIsLoading((prev) => ({ ...prev, tools: true }));
            const response = await get(`/api/match_tools`);

            let data: IMatchTool[] | Response;
            try {
                data = await response.json();
            } catch {
                data = response;
            }
            console.log("Get match tools from provider svc response: ", data);

            if (ignore) {
                return;
            }
            if (response.ok && Array.isArray(data) && data.length > 0) {
                const currentTool = (data as IMatchTool[]).find(
                    (t: IMatchTool) => t.method_name?.toLowerCase() === tool
                );
                setMatchTool(currentTool);
            } else {
                newAlert({
                    message: "Error loading match tools.",
                    color: "danger",
                });
            }
            setIsLoading((prev) => ({ ...prev, tools: false }));
        }

        return () => {
            ignore = true;
        };
    }, [get, newAlert, tool]);

    /** If any match tool action has method_type of "select", then we must first do the
     * select action prior to loading data for both tables */
    const selectAction = matchTool?.actions.find(
        (a) => a.method_type === "select"
    );

    // matchToolData - Call endpoint to call provider service for match tool data for left and
    // right tables if there is no select action
    useEffect(() => {
        if (!tool || !matchTool || selectAction) {
            return;
        }

        ignoreMatchToolData.current = false;

        getMatchToolData(`/api/match_tools/tool_data/${tool}`);

        return () => {
            ignoreMatchToolData.current = true;
        };
    }, [getMatchToolData, matchTool, selectAction, tool]);

    // Reset all state if the tool changes (user navigated to different tool)
    useEffect(() => {
        if (!tool) return;

        setMatchTool(undefined);
        setMatchToolRecords(undefined);
        setMatchToolDataError("");
        setSelectData(undefined);
        setSelection(undefined);
        setIsModalOpen(false);
    }, [tool]);

    useEffect(() => {
        if (matchToolRecords?.message) {
            newAlert({
                message: matchToolRecords.message,
                color: "warning",
                
            });
        }
    }, [matchToolRecords, newAlert]);

    const getSelectData = useCallback(
        async (resetCache: boolean = false) => {
            setIsLoading((prev) => ({ ...prev, selectData: true }));

            let url = `/api/match_tools/tool_data/${selectAction!.method_name}`;
            if (resetCache) {
                url += "?reset_cache=true";
            }
            const response = await get(url);

            let data: Response | ISelection[];
            try {
                data = await response.json();
            } catch {
                data = response;
            }
            console.log("Get data for select action response: ", data);

            if (response.ok) {
                const cacheTime = response.headers.get("cached_at");
                if (cacheTime) {
                    setSelectDataCacheTime(cacheTime);
                } else {
                    setSelectDataCacheTime("");
                }
                setSelectDataError("");
                setSelectData(data as ISelection[]);
            } else {
                // dimension-mrzt3: show WHY. This used to raise a generic toast and leave the
                // modal rendering an empty table, so a failed load read as "there are no jobs".
                const detail =
                    (data as { detail?: string; title?: string })?.detail ||
                    (data as { title?: string })?.title ||
                    `${response.status} ${response.statusText}`;
                setSelectDataError(detail);
                setSelectData([]);
                newAlert({
                    message: `Error loading data for select action: ${detail}`,
                    color: "danger",
                });
            }
            setIsLoading((prev) => ({ ...prev, selectData: false }));
        },
        [get, newAlert, selectAction]
    );

    // selectData - Call endpoint to call provider service for data for select
    // action dropdown if it exists
    useEffect(() => {
        if (!selectAction || selectData) {
            return;
        }
        getSelectData();
    }, [getSelectData, selectAction, selectData]);

    // matchToolData - Call endpoint to call provider service for match tool data for left and
    // right tables when there is a select action and a selection has been made
    useEffect(() => {
        if (!matchTool || !selectAction || !selection) return;

        ignoreMatchToolData.current = false;

        const params = matchTool.parameters;
        const args = params.map((p) => selection[p]).join("/");
        let url = `/api/match_tools/tool_data/${tool}/${args}`;
        if (url.endsWith("/")) {
            url = url.slice(0, -1);
        }

        getMatchToolData(url);

        return () => {
            ignoreMatchToolData.current = true;
        };
    }, [getMatchToolData, matchTool, selectAction, selection, tool]);

    // Select modal - Determines whether the select modal is open based on other state
    useEffect(() => {
        if (selectAction && !selection) {
            setIsModalOpen(true);
        } else {
            setIsModalOpen(false);
        }
    }, [selectAction, selection]);

    // Get the system names from the match tool data
    let system1 = matchToolRecords?.data_set_1?.name;
    let system2 = matchToolRecords?.data_set_2?.name;

    const onSetMatchToolData = (data: IMatchData) => {
        setMatchToolRecords(data);
    };

    const onSetSelection = (selection: ISelection | undefined) => {
        setSelection(selection);
    };

    const onSetModal = (modal: boolean) => {
        setIsModalOpen(modal);
    };

    const isSpinnerShown =
        Object.values(isLoading).some((v) => v) && !isModalOpen;

    return (
        <Container fluid className={styles.container}>
            {isSpinnerShown && (
                <Card
                    style={{
                        position: "absolute",
                        top: "35%",
                        left: "50%",
                        height: "300px",
                        maxWidth: "500px",
                        zIndex: "50",
                        padding: "10px",
                        transform: "translateX(-50%)",
                        boxShadow: "var(--boxShadow)",
                    }}
                >
                    <DimensionSpinner
                        style={{
                            scale: "3",
                            position: "static",
                        }}
                    />
                    <p>
                        Loading large data sets may take over 1 minute with
                        several network requests and 1000s of paginated records.
                    </p>
                </Card>
            )}
            {matchToolDataError && (
                <Alert color="danger" className="mt-3">
                    {matchToolDataError}
                </Alert>
            )}
            {matchTool && matchToolRecords && system1 && system2 && (
                <MatchTools
                    matchTool={matchTool}
                    matchToolData={matchToolRecords}
                    onSetMatchToolData={onSetMatchToolData}
                    onSetModal={onSetModal}
                    system1={system1}
                    system2={system2}
                    selection={selection}
                    getMatchToolData={getMatchToolData}
                />
            )}
            {
                <SelectModal
                    modal={isModalOpen}
                    onSetModal={onSetModal}
                    selectData={selectData || []}
                    onSetSelection={onSetSelection}
                    description={selectAction?.description ?? ""}
                    cacheTime={selectDataCacheTime}
                    getSelectData={getSelectData}
                    isLoading={isLoading.selectData}
                    error={selectDataError}
                />
            }
        </Container>
    );
}
